/**
 * Centralized Date Utilities
 * 
 * This module provides consistent date handling across the application using date-fns.
 * All dates are stored in Firestore as UTC Timestamps and displayed in America/New_York timezone.
 * 
 * @module dateUtils
 */

import { 
  format, 
  parseISO, 
  isValid, 
  isBefore, 
  isAfter,
  isSameDay,
  addHours,
  subHours,
  startOfDay,
  endOfDay,
  addDays,
  subDays
} from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

/**
 * Default timezone for the application
 * @constant {string}
 */
export const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Event buffer time in hours (events shown up to 4 hours after start)
 * @constant {number}
 */
export const EVENT_BUFFER_HOURS = 4;

/**
 * Normalizes any date input to a JavaScript Date object
 * Handles Firestore Timestamps, ISO strings, Date objects, and various formats
 * 
 * @param {*} input - The date input (Firestore Timestamp, string, Date, or object with _seconds)
 * @returns {Date|null} - Normalized Date object or null if invalid
 * 
 * @example
 * normalizeDate(firestoreTimestamp) // Returns Date
 * normalizeDate("2024-01-15T10:00:00Z") // Returns Date
 * normalizeDate(new Date()) // Returns Date
 * normalizeDate({ _seconds: 1234567890 }) // Returns Date
 */
export function normalizeDate(input) {
  if (!input) return null;

  try {
    // Firestore Timestamp with toDate method
    if (typeof input.toDate === 'function') {
      return input.toDate();
    }

    // Firestore Timestamp-like object with _seconds
    if (input._seconds) {
      return new Date(input._seconds * 1000);
    }

    // Firestore Timestamp-like object with seconds
    if (input.seconds) {
      return new Date(input.seconds * 1000);
    }

    // Already a Date object
    if (input instanceof Date) {
      return isValid(input) ? input : null;
    }

    // ISO string or date string
    if (typeof input === 'string') {
      const parsed = parseISO(input);
      if (isValid(parsed)) {
        return parsed;
      }
      // Try direct Date constructor as fallback
      const direct = new Date(input);
      return isValid(direct) ? direct : null;
    }

    // Number (timestamp in milliseconds)
    if (typeof input === 'number') {
      const date = new Date(input);
      return isValid(date) ? date : null;
    }

    return null;
  } catch (error) {
    console.warn('Error normalizing date:', error, 'Input:', input);
    return null;
  }
}

/**
 * Converts a date to ISO string format
 * 
 * @param {*} date - Date input
 * @returns {string|null} - ISO string or null if invalid
 * 
 * @example
 * toISOString(new Date()) // "2024-01-15T10:00:00.000Z"
 */
export function toISOString(date) {
  const normalized = normalizeDate(date);
  return normalized ? normalized.toISOString() : null;
}

/**
 * Formats a date for display using America/New_York timezone
 * 
 * @param {*} date - Date input
 * @param {string} formatStr - Format string (date-fns format)
 * @param {string} timezone - Timezone (defaults to America/New_York)
 * @returns {string|null} - Formatted string or null if invalid
 * 
 * @example
 * formatDisplay(date, 'MMM d, yyyy') // "Jan 15, 2024"
 * formatDisplay(date, 'YYYY-MM-DD HH:mm:ss') // "2024-01-15 10:00:00"
 * formatDisplay(date, 'hh:mm a') // "10:00 AM"
 */
export function formatDisplay(date, formatStr = 'MMM d, yyyy', timezone = DEFAULT_TIMEZONE) {
  const normalized = normalizeDate(date);
  if (!normalized) return null;

  try {
    return formatInTimeZone(normalized, timezone, formatStr);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return null;
  }
}

/**
 * Formats a date for database storage (YYYY-MM-DD HH:mm:ss in NY timezone)
 * This matches the legacy moment format used throughout the app
 * 
 * @param {*} date - Date input
 * @returns {string|null} - Formatted string or null if invalid
 * 
 * @example
 * formatForDatabase(new Date()) // "2024-01-15 10:00:00"
 */
export function formatForDatabase(date) {
  return formatDisplay(date, 'yyyy-MM-dd HH:mm:ss');
}

/**
 * Formats a date for display in a readable format
 * 
 * @param {*} date - Date input
 * @returns {string|null} - Formatted string like "January 15, 2024 at 10:00 AM" or null
 * 
 * @example
 * formatReadable(new Date()) // "January 15, 2024 at 10:00 AM"
 */
export function formatReadable(date) {
  return formatDisplay(date, 'MMMM d, yyyy \'at\' h:mm a');
}

/**
 * Formats a date for event display (compact format with day, date, and time)
 * 
 * @param {*} date - Date input
 * @returns {string} - Formatted string like "Wed, Jan 15, 10:00 AM" or empty string
 * 
 * @example
 * formatEventDate(new Date()) // "Wed, Jan 15, 10:00 AM"
 */
export function formatEventDate(date) {
  const normalized = normalizeDate(date);
  if (!normalized) return "";
  
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: DEFAULT_TIMEZONE,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(normalized);
  } catch (error) {
    console.warn('Error formatting event date:', error);
    return "";
  }
}

/**
 * Compares two events by their main_date for sorting
 * 
 * @param {Object} a - First event
 * @param {Object} b - Second event
 * @returns {number} - Comparison result (-1, 0, or 1)
 * 
 * @example
 * events.sort(compareEventsByDate) // Ascending order
 * events.sort((a, b) => -compareEventsByDate(a, b)) // Descending order
 */
export function compareEventsByDate(a, b) {
  const dateA = normalizeDate(a.main_date);
  const dateB = normalizeDate(b.main_date);
  
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  
  return dateA.getTime() - dateB.getTime();
}

/**
 * Checks if an event date is in the future (including buffer time)
 * Events are considered "upcoming" up to 4 hours after their start time
 * 
 * @param {*} date - Date input
 * @param {Date} now - Reference date (defaults to current time)
 * @returns {boolean} - True if date is upcoming
 * 
 * @example
 * isUpcoming(eventDate) // true if event hasn't ended yet
 */
export function isUpcoming(date, now = new Date()) {
  const normalized = normalizeDate(date);
  if (!normalized) return false;
  
  const bufferCutoff = subHours(now, EVENT_BUFFER_HOURS);
  return isAfter(normalized, bufferCutoff) || isSameDay(normalized, bufferCutoff);
}

/**
 * Checks if a date is in the past
 * 
 * @param {*} date - Date input
 * @param {Date} now - Reference date (defaults to current time)
 * @returns {boolean} - True if date is in the past
 */
export function isPast(date, now = new Date()) {
  return !isUpcoming(date, now);
}

/**
 * Gets the cutoff date for "upcoming" events (current time minus buffer)
 * 
 * @returns {Date} - Cutoff date
 * 
 * @example
 * const cutoff = getEventBufferCutoff() // 4 hours ago
 */
export function getEventBufferCutoff() {
  return subHours(new Date(), EVENT_BUFFER_HOURS);
}

/**
 * Gets current time in New York timezone
 * 
 * @returns {Date} - Current date/time in NY timezone
 */
export function getCurrentNYTime() {
  return toZonedTime(new Date(), DEFAULT_TIMEZONE);
}

/**
 * Converts a date string/object to a Firestore-compatible ISO string
 * Preserves timezone information for NY timezone
 * 
 * @param {*} date - Date input
 * @returns {string|null} - ISO string with timezone offset or null
 * 
 * @example
 * toISOWithTimezone(date) // "2024-01-15T10:00:00-05:00"
 */
export function toISOWithTimezone(date) {
  const normalized = normalizeDate(date);
  if (!normalized) return null;
  
  try {
    return formatInTimeZone(normalized, DEFAULT_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ssXXX");
  } catch (error) {
    console.warn('Error converting to ISO with timezone:', error);
    return toISOString(date);
  }
}

/**
 * Gets start and end of day timestamps for a given date in NY timezone
 * Useful for date range queries
 * 
 * @param {*} date - Date input
 * @returns {Object} - Object with startTimestamp and endTimestamp
 * 
 * @example
 * const { startTimestamp, endTimestamp } = getDayRange(new Date())
 */
export function getDayRange(date) {
  const normalized = normalizeDate(date);
  if (!normalized) {
    return { startTimestamp: null, endTimestamp: null };
  }
  
  const zonedDate = toZonedTime(normalized, DEFAULT_TIMEZONE);
  const dayStart = startOfDay(zonedDate);
  const dayEnd = endOfDay(zonedDate);
  
  return {
    startTimestamp: fromZonedTime(dayStart, DEFAULT_TIMEZONE),
    endTimestamp: fromZonedTime(dayEnd, DEFAULT_TIMEZONE)
  };
}

/**
 * Adds days to a date
 * 
 * @param {*} date - Date input
 * @param {number} days - Number of days to add
 * @returns {Date|null} - New date or null
 */
export function addDaysToDate(date, days) {
  const normalized = normalizeDate(date);
  return normalized ? addDays(normalized, days) : null;
}

/**
 * Subtracts days from a date
 * 
 * @param {*} date - Date input
 * @param {number} days - Number of days to subtract
 * @returns {Date|null} - New date or null
 */
export function subtractDaysFromDate(date, days) {
  const normalized = normalizeDate(date);
  return normalized ? subDays(normalized, days) : null;
}

/**
 * Validates if a date is valid
 * 
 * @param {*} date - Date input
 * @returns {boolean} - True if valid
 */
export function isValidDate(date) {
  return normalizeDate(date) !== null;
}

/**
 * Parses various Firestore timestamp formats
 * Legacy helper for backward compatibility
 * 
 * @deprecated Use normalizeDate instead
 * @param {*} timestamp - Timestamp input
 * @returns {Date|null} - Parsed date or null
 */
export function parseFirestoreTimestamp(timestamp) {
  console.warn('parseFirestoreTimestamp is deprecated. Use normalizeDate instead.');
  return normalizeDate(timestamp);
}
